import React from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { User, Shield, Mail, Briefcase } from 'lucide-react';

export default function ProfilePage() {
  const { user, isAdmin } = useAuth();

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            My Profile
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Your account information and permissions
          </p>
        </div>

        {/* User Info Card */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-8 mb-6">
          <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))] mb-6">
            Account Details
          </h2>

          <div className="space-y-4">
            {/* Name */}
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                <User className="w-5 h-5 text-[hsl(var(--brand-primary))]" />
              </div>
              <div>
                <div className="text-sm text-[hsl(var(--text-secondary))]">Full Name</div>
                <div className="text-lg font-semibold text-[hsl(var(--text-primary))]">
                  {user.name}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                <Mail className="w-5 h-5 text-[hsl(var(--brand-primary))]" />
              </div>
              <div>
                <div className="text-sm text-[hsl(var(--text-secondary))]">Email</div>
                <div className="text-lg font-semibold text-[hsl(var(--text-primary))]">
                  {user.email}
                </div>
              </div>
            </div>

            {/* Role */}
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                <Shield className="w-5 h-5 text-[hsl(var(--brand-primary))]" />
              </div>
              <div>
                <div className="text-sm text-[hsl(var(--text-secondary))]">Fleet Shares Role</div>
                <div className="text-lg font-semibold text-[hsl(var(--text-primary))]">
                  {user.role === 'admin' ? 'Administrator' : 'Employee'}
                  <span className={`ml-3 px-3 py-1 rounded-full text-xs font-medium ${
                    user.role === 'admin'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {user.role.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* App Role */}
            {user.app_role && (
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                  <Briefcase className="w-5 h-5 text-[hsl(var(--brand-primary))]" />
                </div>
                <div>
                  <div className="text-sm text-[hsl(var(--text-secondary))]">HUB App Role</div>
                  <div className="text-lg font-semibold text-[hsl(var(--text-primary))]">
                    {user.app_role}
                  </div>
                </div>
              </div>
            )}

            {/* User ID */}
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                <span className="text-xs font-mono text-[hsl(var(--brand-primary))]">#</span>
              </div>
              <div>
                <div className="text-sm text-[hsl(var(--text-secondary))]">User ID</div>
                <div className="text-sm font-mono text-[hsl(var(--text-primary))]">
                  {user.user_id}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Card */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-8">
          <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))] mb-6">
            Permissions
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[hsl(var(--text-secondary))]">View Fleet Assets</span>
              <span className="text-green-600 font-semibold">✓ Allowed</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[hsl(var(--text-secondary))]">Buy Shares</span>
              <span className="text-green-600 font-semibold">✓ Allowed</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[hsl(var(--text-secondary))]">View Portfolio</span>
              <span className="text-green-600 font-semibold">✓ Allowed</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[hsl(var(--text-secondary))]">Admin Panel Access</span>
              <span className={isAdmin() ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                {isAdmin() ? '✓ Allowed' : '✗ Restricted'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[hsl(var(--text-secondary))]">Manage Assets</span>
              <span className={isAdmin() ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                {isAdmin() ? '✓ Allowed' : '✗ Restricted'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[hsl(var(--text-secondary))]">Approve Requests</span>
              <span className={isAdmin() ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                {isAdmin() ? '✓ Allowed' : '✗ Restricted'}
              </span>
            </div>
          </div>

          {!isAdmin() && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Need admin access?</strong> Contact your HUB administrator to upgrade your role to Master Admin or Admin.
              </p>
            </div>
          )}
        </div>

        {/* Debug Info (for troubleshooting) */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Debug Information</h3>
          <pre className="text-xs font-mono text-gray-600 bg-white p-4 rounded border border-gray-200 overflow-x-auto">
            {JSON.stringify(user, null, 2)}
          </pre>
        </div>
      </main>
    </div>
  );
}
